import { useRef, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LayoutDashboard, ShieldCheck, LogOut, ChevronDown, UserCircle } from 'lucide-react';
import { useSession, signOut } from '@/lib/auth/auth-client';

const GRAD = '#5048E5';

export default function AccountMenu() {
  const { user, isPending } = useSession();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Only enable hover-to-open on devices with a real pointer (mouse/trackpad).
  // On touch devices hover-to-open causes the dreaded "tap twice" bug.
  const canHover = useRef(false);

  useEffect(() => {
    canHover.current =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(hover: hover) and (pointer: fine)').matches;
  }, []);

  // Close on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, []);

  function handleMouseEnter() {
    if (!canHover.current) return;
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  }

  function handleMouseLeave() {
    if (!canHover.current) return;
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  }

  async function handleLogout() {
    await signOut();
    navigate('/login');
  }

  if (isPending) return null;
  if (!user) return null;

  const initials = user.name
    ? user.name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
    : user.email?.[0]?.toUpperCase() ?? '?';

  const isAdmin = (user as any).isAdmin;

  return (
    <div ref={ref} className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>

      {/* Trigger button */}
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold transition-all hover:opacity-90"
        style={{
          background: 'var(--glass-bg)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid var(--glass-border)',
          boxShadow: 'var(--glass-shadow)',
          color: 'var(--glass-color)',
          fontFamily: 'Plus Jakarta Sans, sans-serif',
        }}
        aria-haspopup="true"
        aria-expanded={open}
      >
        {/* Avatar */}
        {user.image ? (
          <img src={user.image} alt={user.name ?? 'Avatar'} width={24} height={24} className="h-6 w-6 rounded-full object-cover shrink-0" />
        ) : (
          <span
            className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold shrink-0"
            style={{ background: GRAD, color: '#ffffff', fontFamily: 'Outfit, sans-serif' }}>
            {initials}
          </span>
        )}
        <span className="hidden sm:block">My Account</span>
        <ChevronDown
          size={13}
          className="transition-transform duration-200"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute right-0 top-full z-50 mt-2 w-52 rounded-2xl py-2 shadow-xl"
          style={{
            background: 'hsl(var(--card))',
            border: '1px solid hsl(var(--border))',
            fontFamily: 'Plus Jakarta Sans, sans-serif',
          }}
        >
          {/* User info */}
          <div className="border-b px-4 pb-3 pt-2" style={{ borderColor: 'hsl(var(--border))' }}>
            <p className="truncate text-sm font-semibold" style={{ color: 'hsl(var(--foreground))' }}>
              {user.name || 'My Account'}
            </p>
            <p className="truncate text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>
              {user.email}
            </p>
          </div>

          {/* Links */}
          <div className="mt-1 flex flex-col">
            {!isAdmin && (
              <DropdownLink to="/dashboard" icon={<LayoutDashboard size={14} />} label="Dashboard" onClick={() => setOpen(false)} />
            )}
            {isAdmin && (
              <DropdownLink to="/admin" icon={<ShieldCheck size={14} />} label="Admin Panel" onClick={() => setOpen(false)} />
            )}
            <DropdownLink to="/profile" icon={<UserCircle size={14} />} label="My Profile" onClick={() => setOpen(false)} />
          </div>

          {/* Logout */}
          <div className="mt-1 border-t pt-1" style={{ borderColor: 'hsl(var(--border))' }}>
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-2.5 rounded-xl px-4 py-2.5 text-sm transition-colors hover:opacity-80"
              style={{ color: 'hsl(0 70% 55%)', fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              <LogOut size={14} />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function DropdownLink({ to, icon, label, onClick }: { to: string; icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex items-center gap-2.5 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors hover:opacity-80"
      style={{ color: 'hsl(var(--foreground))', fontFamily: 'Plus Jakarta Sans, sans-serif' }}
    >
      <span style={{ color: 'hsl(var(--muted-foreground))' }}>{icon}</span>
      {label}
    </Link>
  );
}
